import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '@/lib/api'
import { formatPercent, formatNumber, formatDate } from '@/lib/utils'
import { MetricCard, LoadingSpinner } from '@/components/ui'
import { LineChart, HeatmapTable } from '@/components/charts'

type Tab = 'returns' | 'correlations' | 'build' | 'saved' | 'portfolio'

const TABS: { id: Tab; label: string }[] = [
  { id: 'returns', label: 'Factor Returns' },
  { id: 'correlations', label: 'Correlations' },
  { id: 'build', label: 'Build Model' },
  { id: 'saved', label: 'Saved Models' },
  { id: 'portfolio', label: 'Portfolio Exposures' },
]

export default function FactorModeling() {
  const [activeTab, setActiveTab] = useState<Tab>('returns')
  const [months, setMonths] = useState(36)
  const [selectedFactors, setSelectedFactors] = useState<string[]>([])
  const dataTab = activeTab === 'returns' || activeTab === 'correlations' || activeTab === 'portfolio'

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Factor Modeling</h1>
          <p className="text-muted-foreground">Factor analysis and portfolio exposures</p>
        </div>
        {dataTab && (
          <div className="flex items-center gap-2">
            <label className="text-sm text-muted-foreground">Period:</label>
            <select
              className="h-9 px-3 border border-input rounded-md bg-background text-sm"
              value={months}
              onChange={(e) => setMonths(parseInt(e.target.value))}
            >
              <option value={12}>1 Year</option>
              <option value={24}>2 Years</option>
              <option value={36}>3 Years</option>
              <option value={60}>5 Years</option>
              <option value={120}>10 Years</option>
            </select>
          </div>
        )}
      </div>

      {dataTab && (
        <div className="flex items-start gap-3">
          <label className="text-sm text-muted-foreground whitespace-nowrap pt-2">Factors:</label>
          <FactorPicker selected={selectedFactors} onChange={setSelectedFactors} />
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 border-b border-border">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeTab === tab.id
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'returns' && <FactorReturnsTab months={months} factors={selectedFactors} />}
      {activeTab === 'correlations' && <CorrelationsTab months={months} factors={selectedFactors} />}
      {activeTab === 'build' && <BuildModelTab />}
      {activeTab === 'saved' && <SavedModelsTab />}
      {activeTab === 'portfolio' && <PortfolioExposuresTab months={months} factors={selectedFactors} />}
    </div>
  )
}

/* ─── Shared factor picker (search + multi-select over the ~1500 factors) ─── */

type FactorCoverage = { name: string; start?: string; end?: string; months?: number }

// Build a name -> coverage lookup from the /available-factors `catalog`.
function buildCoverageMap(catalog?: FactorCoverage[]): Record<string, FactorCoverage> {
  const m: Record<string, FactorCoverage> = {}
  ;(catalog || []).forEach((c) => { m[c.name] = c })
  return m
}

// Small right-aligned badge showing a factor's data depth; amber when < 12
// months (too short to regress most funds over a normal lookback).
function CoverageBadge({ cov }: { cov?: FactorCoverage }) {
  if (!cov || !cov.months) return null
  const short = cov.months < 12
  const range = cov.start && cov.end ? `${cov.start.slice(0, 7)}→${cov.end.slice(0, 7)}` : ''
  return (
    <span
      className={`ml-auto pl-2 text-[10px] whitespace-nowrap ${short ? 'text-amber-600 dark:text-amber-400 font-medium' : 'text-muted-foreground'}`}
      title={short ? 'Short history — may not overlap a fund enough to regress' : undefined}
    >
      {range ? `${range} · ` : ''}{cov.months}m{short ? ' ⚠' : ''}
    </span>
  )
}

function FactorPicker({ selected, onChange }: { selected: string[]; onChange: (f: string[]) => void }) {
  const [search, setSearch] = useState('')
  const { data } = useQuery({
    queryKey: ['available-factors'],
    queryFn: () => api.get('/factors/available-factors').then((r) => r.data),
  })
  const all: string[] = data?.factors || []
  const covMap = buildCoverageMap(data?.catalog)
  const q = search.trim().toLowerCase()
  const matches = q.length >= 1
    ? all.filter((f) => f.toLowerCase().includes(q) && !selected.includes(f)).slice(0, 20)
    : []

  return (
    <div className="relative w-full max-w-2xl">
      {selected.length > 0 && (
        <div className="flex flex-wrap items-center gap-1 mb-2">
          {selected.map((f) => (
            <span key={f} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-primary/10 text-primary text-xs">
              {f}
              <button onClick={() => onChange(selected.filter((x) => x !== f))} className="hover:text-red-600" aria-label={`Remove ${f}`}>×</button>
            </span>
          ))}
          <button onClick={() => onChange([])} className="text-xs text-muted-foreground hover:text-foreground underline ml-1">
            Clear all
          </button>
        </div>
      )}
      <input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder={all.length ? `Search ${all.length} factors to filter…` : 'Search factors…'}
        className="h-9 w-full px-3 border border-input rounded-md bg-background text-sm"
      />
      {matches.length > 0 && (
        <div className="absolute z-20 mt-1 w-full max-h-72 overflow-y-auto border border-border rounded-md bg-card shadow-lg">
          {matches.map((f) => (
            <button
              key={f}
              onClick={() => { onChange([...selected, f]); setSearch('') }}
              className="flex items-center w-full text-left px-3 py-1.5 text-sm hover:bg-accent"
            >
              <span className="truncate">{f}</span>
              <CoverageBadge cov={covMap[f]} />
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

/* ─── Factor Returns Tab ─── */

function FactorReturnsTab({ months, factors }: { months: number; factors: string[] }) {
  const fq = factors.length ? `&factors=${encodeURIComponent(factors.join(','))}` : ''
  const { data, isLoading } = useQuery({
    queryKey: ['factor-data', months, factors.join(',')],
    queryFn: () => api.get(`/factors?months=${months}${fq}`).then((r) => r.data),
  })

  if (isLoading) return <LoadingSpinner />
  if (!data?.factors?.length) return <Empty msg="No factor data available" />

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      {!factors.length && data.total_available > data.factors.length && (
        <p className="text-xs text-muted-foreground px-3 pt-2">
          Showing {data.factors.length} of {data.total_available} factors — search above to pick specific ones.
        </p>
      )}
      <div className="overflow-x-auto">
        <table className="data-table">
          <thead>
            <tr>
              <th>Date</th>
              {data.factors.map((f: string) => (
                <th key={f} className="text-right whitespace-nowrap">{f}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.data.slice(0, 60).map((row: any) => (
              <tr key={row.date}>
                <td className="whitespace-nowrap">{formatDate(row.date)}</td>
                {data.factors.map((f: string) => (
                  <td
                    key={f}
                    className={`text-right font-mono ${
                      row[f] == null ? 'text-muted-foreground' : row[f] >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}
                  >
                    {row[f] != null ? formatPercent(row[f]) : '-'}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

/* ─── Correlations Tab ─── */

function CorrelationsTab({ months, factors }: { months: number; factors: string[] }) {
  const fq = factors.length ? `&factors=${encodeURIComponent(factors.join(','))}` : ''
  const { data, isLoading } = useQuery({
    queryKey: ['factor-correlations', months, factors.join(',')],
    queryFn: () => api.get(`/factors/correlations?months=${months}${fq}`).then((r) => r.data),
  })

  if (isLoading) return <LoadingSpinner />
  if (!data?.factors?.length) return <Empty msg="No correlation data available" />

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <h2 className="text-lg font-semibold mb-1">Factor Correlation Matrix</h2>
      {!factors.length && data.total_available > data.factors.length && (
        <p className="text-xs text-muted-foreground mb-4">
          Showing {data.factors.length} of {data.total_available} factors — search above to compare specific ones.
        </p>
      )}
      <div className="overflow-x-auto">
        <HeatmapTable rowLabels={data.factors} colLabels={data.factors} data={data.matrix} colorScale="diverging" />
      </div>
    </div>
  )
}

/* ─── Build Model Tab ─── */

function BuildModelTab() {
  const queryClient = useQueryClient()
  const [step, setStep] = useState(1)

  // Step 1: Fund selection
  const [fundSearch, setFundSearch] = useState('')
  const [selectedFund, setSelectedFund] = useState<{ id: number; name: string; source: string } | null>(null)

  // Step 2: Factor selection
  const [selectedFactors, setSelectedFactors] = useState<string[]>([])

  // Step 3: Config
  const [regMonths, setRegMonths] = useState(36)
  const [constraintsEnabled, setConstraintsEnabled] = useState(false)
  const [constraints, setConstraints] = useState<Record<string, { min: string; max: string }>>({})

  // Step 4: Results
  const [regressionResults, setRegressionResults] = useState<any>(null)

  // Save dialog
  const [showSaveDialog, setShowSaveDialog] = useState(false)
  const [modelName, setModelName] = useState('')
  const [modelDescription, setModelDescription] = useState('')

  // Fund search query
  // Empty search -> backend returns the portfolio holdings as a default list.
  const { data: fundResults } = useQuery({
    queryKey: ['fund-search', fundSearch],
    queryFn: () => api.get(`/demo-portfolios/search/funds?q=${encodeURIComponent(fundSearch)}&limit=50`).then((r) => r.data),
  })

  // Available factors
  const { data: factorsData } = useQuery({
    queryKey: ['available-factors'],
    queryFn: () => api.get('/factors/available-factors').then((r) => r.data),
  })
  const covMap = buildCoverageMap(factorsData?.catalog)
  // How many currently-selected factors have < 12 months of history
  const shortSelected = selectedFactors.filter((f) => (covMap[f]?.months ?? 99) < 12)

  // Run regression mutation
  const regressMutation = useMutation({
    mutationFn: (payload: any) => {
      const endpoint = payload.constraints ? '/factors/regress-constrained' : '/factors/regress'
      return api.post(endpoint, payload).then((r) => r.data)
    },
    onSuccess: (data) => {
      setRegressionResults(data)
      setStep(4)
    },
  })

  // Save model mutation
  const saveMutation = useMutation({
    mutationFn: (payload: any) => api.post('/factors/models', payload).then((r) => r.data),
    onSuccess: () => {
      setShowSaveDialog(false)
      setModelName('')
      setModelDescription('')
      queryClient.invalidateQueries({ queryKey: ['saved-models'] })
    },
  })

  const handleSelectFund = (fund: any) => {
    setSelectedFund({ id: fund.fund_id, name: fund.fund_name, source: fund.data_source || 'alternatives' })
    setStep(2)
  }

  const toggleFactor = (factor: string) => {
    setSelectedFactors((prev) =>
      prev.includes(factor) ? prev.filter((f) => f !== factor) : [...prev, factor]
    )
  }

  const handleRunRegression = () => {
    if (!selectedFund || selectedFactors.length === 0) return
    const payload: any = {
      fund_id: selectedFund.id,
      fund_source: selectedFund.source,
      factor_names: selectedFactors,
      months: regMonths,
    }
    if (constraintsEnabled) {
      const constraintsList = selectedFactors
        .filter((f) => constraints[f]?.min || constraints[f]?.max)
        .map((f) => ({
          factor: f,
          min: constraints[f]?.min ? parseFloat(constraints[f].min) : null,
          max: constraints[f]?.max ? parseFloat(constraints[f].max) : null,
        }))
      if (constraintsList.length > 0) {
        payload.constraints = constraintsList
      }
    }
    regressMutation.mutate(payload)
  }

  const handleSave = () => {
    if (!selectedFund || !regressionResults || !modelName.trim()) return
    saveMutation.mutate({
      name: modelName.trim(),
      description: modelDescription.trim() || null,
      fund_id: selectedFund.id,
      fund_source: selectedFund.source,
      factor_names: selectedFactors,
      months: regMonths,
      regression_results: regressionResults,
    })
  }

  return (
    <div className="space-y-6">
      {/* Step indicators */}
      <div className="flex items-center gap-2">
        {[1, 2, 3, 4].map((s) => (
          <button
            key={s}
            onClick={() => { if (s <= step) setStep(s) }}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              s === step
                ? 'bg-primary text-primary-foreground'
                : s < step
                ? 'bg-primary/20 text-primary cursor-pointer'
                : 'bg-muted text-muted-foreground cursor-default'
            }`}
          >
            <span className="w-6 h-6 rounded-full flex items-center justify-center text-xs border border-current">
              {s}
            </span>
            {s === 1 && 'Select Fund'}
            {s === 2 && 'Select Factors'}
            {s === 3 && 'Configure'}
            {s === 4 && 'Results'}
          </button>
        ))}
      </div>

      {/* Step 1: Select Fund */}
      {step === 1 && (
        <div className="bg-card border border-border rounded-lg p-6 space-y-4">
          <h2 className="text-lg font-semibold">Select Fund</h2>
          <input
            type="text"
            placeholder="Search funds by name — or pick from your portfolio below…"
            className="w-full max-w-md h-10 px-3 border border-input rounded-md bg-background text-sm"
            value={fundSearch}
            onChange={(e) => setFundSearch(e.target.value)}
          />
          {!fundSearch && (
            <p className="text-xs text-muted-foreground">Your portfolio funds — type to search all funds:</p>
          )}
          {fundResults?.length > 0 ? (
            <div className="max-h-72 overflow-y-auto border border-border rounded-md max-w-md">
              {fundResults.map((fund: any) => (
                <button
                  key={`${fund.data_source}-${fund.fund_id}`}
                  onClick={() => handleSelectFund(fund)}
                  className={`w-full text-left px-4 py-2 hover:bg-muted transition-colors border-b border-border last:border-b-0 text-sm ${
                    selectedFund?.id === fund.fund_id && selectedFund?.source === fund.data_source
                      ? 'bg-primary/10 text-primary'
                      : ''
                  }`}
                >
                  <div className="font-medium">{fund.fund_name}</div>
                  <div className="text-xs text-muted-foreground">
                    ID: {fund.fund_id} | Source: {fund.data_source || 'alternatives'}
                  </div>
                </button>
              ))}
            </div>
          ) : (
            fundSearch.length >= 2 && <p className="text-sm text-muted-foreground">No funds found for “{fundSearch}”.</p>
          )}
          {selectedFund && (
            <div className="p-3 bg-primary/5 border border-primary/20 rounded-md">
              <span className="text-sm font-medium">Selected: </span>
              <span className="text-sm">{selectedFund.name}</span>
              <span className="text-xs text-muted-foreground ml-2">({selectedFund.source})</span>
            </div>
          )}
        </div>
      )}

      {/* Step 2: Select Factors */}
      {step === 2 && (
        <div className="bg-card border border-border rounded-lg p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Select Factors</h2>
            <span className="text-sm text-muted-foreground">{selectedFactors.length} selected</span>
          </div>
          <p className="text-xs text-muted-foreground -mt-1">
            The <span className="text-amber-600 dark:text-amber-400">Nm ⚠</span> badge is a factor's months of history — factors shorter than the fund's lookback won't have enough overlap to regress.
          </p>
          {factorsData?.factors ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 max-h-96 overflow-y-auto">
              {factorsData.factors.map((factor: string) => (
                <label
                  key={factor}
                  className={`flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer transition-colors text-sm ${
                    selectedFactors.includes(factor)
                      ? 'bg-primary/10 border border-primary/30'
                      : 'hover:bg-muted border border-transparent'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedFactors.includes(factor)}
                    onChange={() => toggleFactor(factor)}
                    className="rounded border-input"
                  />
                  <span className="truncate">{factor}</span>
                  <CoverageBadge cov={covMap[factor]} />
                </label>
              ))}
            </div>
          ) : (
            <LoadingSpinner />
          )}
          {shortSelected.length > 0 && (
            <div className="bg-amber-500/10 border border-amber-500/40 text-amber-700 dark:text-amber-400 rounded-md px-3 py-2 text-xs">
              {shortSelected.length} selected factor{shortSelected.length > 1 ? 's have' : ' has'} under 12 months of history
              ({shortSelected.join(', ')}). {shortSelected.length > 1 ? 'They' : 'It'} may be dropped or block the regression if the overlap with the fund is too short.
            </div>
          )}
          <div className="flex justify-between pt-4 border-t border-border">
            <button
              onClick={() => setStep(1)}
              className="px-4 py-2 text-sm border border-input rounded-md hover:bg-muted"
            >
              Back
            </button>
            <button
              onClick={() => setStep(3)}
              disabled={selectedFactors.length === 0}
              className="px-4 py-2 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Configure */}
      {step === 3 && (
        <div className="bg-card border border-border rounded-lg p-6 space-y-6">
          <h2 className="text-lg font-semibold">Configure Regression</h2>

          <div className="space-y-4 max-w-md">
            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-2">
                Lookback Period: {regMonths} months
              </label>
              <input
                type="range"
                min={12}
                max={120}
                step={6}
                value={regMonths}
                onChange={(e) => setRegMonths(parseInt(e.target.value))}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-muted-foreground mt-1">
                <span>12m</span>
                <span>36m</span>
                <span>60m</span>
                <span>120m</span>
              </div>
            </div>

            <div className="p-3 bg-muted rounded-md text-sm space-y-1">
              <div><span className="font-medium">Fund:</span> {selectedFund?.name}</div>
              <div><span className="font-medium">Source:</span> {selectedFund?.source}</div>
              <div><span className="font-medium">Factors:</span> {selectedFactors.join(', ')}</div>
              <div><span className="font-medium">Period:</span> {regMonths} months</div>
            </div>
          </div>

          {/* Beta Constraints */}
          <div className="space-y-3">
            <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
              <input
                type="checkbox"
                checked={constraintsEnabled}
                onChange={(e) => setConstraintsEnabled(e.target.checked)}
                className="rounded border-input"
              />
              Enable beta constraints (SLSQP optimizer)
            </label>
            {constraintsEnabled && (
              <div className="border border-border rounded-md overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-muted/50">
                      <th className="text-left px-3 py-2">Factor</th>
                      <th className="text-center px-3 py-2 w-32">Min</th>
                      <th className="text-center px-3 py-2 w-32">Max</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedFactors.map((factor) => (
                      <tr key={factor} className="border-t border-border/50">
                        <td className="px-3 py-1.5 text-xs">{factor}</td>
                        <td className="px-3 py-1.5">
                          <input
                            type="number"
                            step="0.01"
                            placeholder="-Inf"
                            className="w-full h-7 px-2 text-xs border border-input rounded bg-background text-center font-mono"
                            value={constraints[factor]?.min ?? ''}
                            onChange={(e) => setConstraints((prev) => ({
                              ...prev,
                              [factor]: { ...prev[factor], min: e.target.value, max: prev[factor]?.max ?? '' },
                            }))}
                          />
                        </td>
                        <td className="px-3 py-1.5">
                          <input
                            type="number"
                            step="0.01"
                            placeholder="+Inf"
                            className="w-full h-7 px-2 text-xs border border-input rounded bg-background text-center font-mono"
                            value={constraints[factor]?.max ?? ''}
                            onChange={(e) => setConstraints((prev) => ({
                              ...prev,
                              [factor]: { ...prev[factor], max: e.target.value, min: prev[factor]?.min ?? '' },
                            }))}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="px-3 py-1.5 text-[10px] text-muted-foreground bg-muted/30 border-t border-border/50">
                  Leave empty for unconstrained. Note: constrained regression does not produce standard errors or p-values.
                </div>
              </div>
            )}
          </div>

          {regressMutation.error && (
            <div className="p-3 bg-destructive/10 border border-destructive/30 rounded-md text-sm text-destructive">
              {(regressMutation.error as any)?.response?.data?.detail || 'Regression failed'}
            </div>
          )}

          <div className="flex justify-between pt-4 border-t border-border">
            <button
              onClick={() => setStep(2)}
              className="px-4 py-2 text-sm border border-input rounded-md hover:bg-muted"
            >
              Back
            </button>
            <button
              onClick={handleRunRegression}
              disabled={regressMutation.isPending}
              className="px-4 py-2 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50"
            >
              {regressMutation.isPending ? 'Running...' : 'Run Regression'}
            </button>
          </div>
        </div>
      )}

      {/* Step 4: Results */}
      {step === 4 && regressionResults && (
        <RegressionResultsView
          results={regressionResults}
          onSave={() => setShowSaveDialog(true)}
          onBack={() => setStep(3)}
        />
      )}

      {/* Save Dialog */}
      {showSaveDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card border border-border rounded-lg p-6 w-full max-w-md space-y-4">
            <h3 className="text-lg font-semibold">Save Factor Model</h3>
            <div>
              <label className="block text-sm font-medium mb-1">Name</label>
              <input
                type="text"
                className="w-full h-10 px-3 border border-input rounded-md bg-background text-sm"
                value={modelName}
                onChange={(e) => setModelName(e.target.value)}
                placeholder="e.g., Fund ABC - 3 Factor Model"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Description (optional)</label>
              <textarea
                className="w-full h-20 px-3 py-2 border border-input rounded-md bg-background text-sm resize-none"
                value={modelDescription}
                onChange={(e) => setModelDescription(e.target.value)}
                placeholder="Notes about this model..."
              />
            </div>
            {saveMutation.error && (
              <div className="text-sm text-destructive">
                {(saveMutation.error as any)?.response?.data?.detail || 'Save failed'}
              </div>
            )}
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowSaveDialog(false)}
                className="px-4 py-2 text-sm border border-input rounded-md hover:bg-muted"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={!modelName.trim() || saveMutation.isPending}
                className="px-4 py-2 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50"
              >
                {saveMutation.isPending ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

/* ─── Regression Results View (shared between Build and Saved) ─── */

function RegressionResultsView({
  results,
  onSave,
  onBack,
}: {
  results: any
  onSave?: () => void
  onBack?: () => void
}) {
  return (
    <div className="space-y-6">
      {/* Dropped-factor warning: factors with no overlapping data are skipped, not fatal */}
      {results.dropped_factors?.length > 0 && (
        <div className="bg-amber-500/10 border border-amber-500/40 text-amber-700 dark:text-amber-400 rounded-lg px-4 py-3 text-sm">
          <span className="font-semibold">Skipped {results.dropped_factors.length} factor{results.dropped_factors.length > 1 ? 's' : ''} that lacked enough overlapping data for this fund/period:</span>{' '}
          {results.dropped_factors.join(', ')}. The regression ran on the remaining factors.
        </div>
      )}

      {/* Summary metrics */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <MetricCard label="Alpha (ann.)" value={formatPercent(results.alpha != null ? results.alpha * 12 : null)} />
        <MetricCard label="R-squared" value={formatNumber(results.r_squared)} />
        <MetricCard label="Adj R-squared" value={formatNumber(results.adj_r_squared)} />
        <MetricCard label="Durbin-Watson" value={formatNumber(results.durbin_watson)} />
        <MetricCard label="F-statistic" value={formatNumber(results.f_stat)} />
        <MetricCard label="Observations" value={String(results.n_obs)} />
      </div>

      {/* Coefficients table */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="p-4 border-b border-border flex items-center justify-between">
          <h2 className="text-lg font-semibold">Factor Coefficients</h2>
          <div className="flex gap-2">
            {onBack && (
              <button
                onClick={onBack}
                className="px-3 py-1.5 text-sm border border-input rounded-md hover:bg-muted"
              >
                Back
              </button>
            )}
            {onSave && (
              <button
                onClick={onSave}
                className="px-3 py-1.5 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
              >
                Save Model
              </button>
            )}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr>
                <th>Factor</th>
                <th className="text-right">Beta</th>
                <th className="text-right">Std Error</th>
                <th className="text-right">t-stat</th>
                <th className="text-right">p-value</th>
                <th className="text-right" title="Newey-West HAC p-value — robust to autocorrelation/heteroscedasticity">p (HAC)</th>
                <th className="text-right" title="Benjamini-Hochberg FDR-adjusted p-value across the selected factors">q (BH)</th>
                <th className="text-right">VIF</th>
              </tr>
            </thead>
            <tbody>
              {/* Intercept row */}
              <tr className="bg-muted/30">
                <td className="font-medium italic">Intercept (Alpha)</td>
                <td className="text-right font-mono">{formatNumber(results.alpha, 4)}</td>
                <td className="text-right font-mono">-</td>
                <td className="text-right font-mono">{formatNumber(results.alpha_t_stat, 2)}</td>
                <td className={`text-right font-mono ${getPValueColor(results.alpha_p_value)}`}>
                  {formatPValue(results.alpha_p_value)}
                </td>
                <td className={`text-right font-mono ${getPValueColor(results.alpha_hac_p_value)}`}>
                  {results.alpha_hac_p_value != null ? formatPValue(results.alpha_hac_p_value) : '-'}
                </td>
                <td className="text-right font-mono">-</td>
                <td className="text-right font-mono">-</td>
              </tr>
              {results.coefficients?.map((coeff: any) => (
                <tr key={coeff.factor}>
                  <td className="font-medium">
                    {coeff.factor}
                    {coeff.significant_bh === true && (
                      <span className="ml-1 rounded bg-green-100 px-1 text-[10px] text-green-700" title="Survives Benjamini-Hochberg FDR control">BH</span>
                    )}
                  </td>
                  <td className="text-right font-mono">{formatNumber(coeff.beta, 4)}</td>
                  <td className="text-right font-mono">{formatNumber(coeff.std_error, 4)}</td>
                  <td className="text-right font-mono">{formatNumber(coeff.t_stat, 2)}</td>
                  <td className={`text-right font-mono ${getPValueColor(coeff.p_value)}`}>
                    {formatPValue(coeff.p_value)}
                  </td>
                  <td className={`text-right font-mono ${getPValueColor(coeff.hac_p_value)}`}>
                    {coeff.hac_p_value != null ? formatPValue(coeff.hac_p_value) : '-'}
                  </td>
                  <td className={`text-right font-mono ${coeff.significant_bh ? 'text-green-600' : 'text-muted-foreground'}`}>
                    {coeff.p_adjusted_bh != null ? formatPValue(coeff.p_adjusted_bh) : '-'}
                  </td>
                  <td className={`text-right font-mono ${getVifColor(coeff.vif)}`}>
                    {coeff.vif != null ? formatNumber(coeff.vif, 2) : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Multiple-testing / robustness summary */}
          {results.multiple_testing?.available && (
            <div className="mt-3 rounded-md border border-border bg-muted/20 p-3 text-xs">
              <div className="mb-1 font-medium">Factor selection robustness</div>
              <div className="flex flex-wrap gap-4">
                <span>Significant at 5%: <b>{results.multiple_testing.comparison?.naive_significant}</b> naive</span>
                <span>· <b>{results.multiple_testing.n_rejected}</b> after Benjamini-Hochberg (FDR)</span>
                <span>· <b>{results.multiple_testing.comparison?.bonferroni_significant}</b> after Bonferroni</span>
                {results.hac?.available && <span>· HAC lags: <b>{results.hac.maxlags}</b></span>}
              </div>
              <div className="mt-1 text-muted-foreground">
                With {results.multiple_testing.n_tests} factors tested, raw p-values overstate significance.
                BH controls the false-discovery rate; HAC corrects standard errors for autocorrelated returns.
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Regression Diagnostics */}
      {(results.jarque_bera || results.breusch_pagan || results.aic != null || results.bic != null) && (
        <div className="bg-card border border-border rounded-lg p-6">
          <h2 className="text-lg font-semibold mb-4">Regression Diagnostics</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {results.aic != null && (
              <div>
                <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">AIC</div>
                <div className="text-lg font-mono font-semibold">{formatNumber(results.aic, 2)}</div>
              </div>
            )}
            {results.bic != null && (
              <div>
                <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">BIC</div>
                <div className="text-lg font-mono font-semibold">{formatNumber(results.bic, 2)}</div>
              </div>
            )}
            {results.jarque_bera && (
              <div>
                <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Jarque-Bera (Normality)</div>
                <div className="text-lg font-mono font-semibold">{formatNumber(results.jarque_bera.statistic, 2)}</div>
                <div className={`text-xs font-mono ${results.jarque_bera.p_value != null && results.jarque_bera.p_value > 0.05 ? 'text-green-600' : 'text-red-600'}`}>
                  p = {formatPValue(results.jarque_bera.p_value)}
                  {results.jarque_bera.p_value != null && (results.jarque_bera.p_value > 0.05 ? ' (normal)' : ' (non-normal)')}
                </div>
              </div>
            )}
            {results.breusch_pagan && (
              <div>
                <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Breusch-Pagan (Homoscedasticity)</div>
                <div className="text-lg font-mono font-semibold">{formatNumber(results.breusch_pagan.statistic, 2)}</div>
                <div className={`text-xs font-mono ${results.breusch_pagan.p_value != null && results.breusch_pagan.p_value > 0.05 ? 'text-green-600' : 'text-red-600'}`}>
                  p = {formatPValue(results.breusch_pagan.p_value)}
                  {results.breusch_pagan.p_value != null && (results.breusch_pagan.p_value > 0.05 ? ' (homoscedastic)' : ' (heteroscedastic)')}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Residuals chart */}
      {results.residuals?.length > 0 && (
        <div className="bg-card border border-border rounded-lg p-6">
          <h2 className="text-lg font-semibold mb-4">Residuals Over Time</h2>
          <LineChart
            data={results.residuals}
            xKey="date"
            lines={[{ key: 'value', name: 'Residual', color: 'hsl(0, 84%, 60%)' }]}
            height={250}
            yTickFormatter={(v: number) => formatPercent(v)}
          />
        </div>
      )}
    </div>
  )
}

/* ─── Saved Models Tab ─── */

function SavedModelsTab() {
  const queryClient = useQueryClient()
  const [selectedModelId, setSelectedModelId] = useState<number | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['saved-models'],
    queryFn: () => api.get('/factors/models').then((r) => r.data),
  })

  const { data: modelDetail, isLoading: detailLoading } = useQuery({
    queryKey: ['saved-model-detail', selectedModelId],
    queryFn: () => api.get(`/factors/models/${selectedModelId}`).then((r) => r.data),
    enabled: selectedModelId != null,
  })

  const deleteMutation = useMutation({
    mutationFn: (id: number) => api.delete(`/factors/models/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['saved-models'] })
      setSelectedModelId(null)
    },
  })

  if (isLoading) return <LoadingSpinner />
  if (!data?.models?.length) return <Empty msg="No saved models yet. Build a model first!" />

  // If viewing a model detail
  if (selectedModelId != null && modelDetail) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <button
              onClick={() => setSelectedModelId(null)}
              className="text-sm text-primary hover:underline mb-2 block"
            >
              &larr; Back to list
            </button>
            <h2 className="text-lg font-semibold">{modelDetail.name}</h2>
            {modelDetail.description && (
              <p className="text-sm text-muted-foreground">{modelDetail.description}</p>
            )}
          </div>
          <div className="text-sm text-muted-foreground">
            <div>Factors: {modelDetail.factor_names?.join(', ')}</div>
            <div>Period: {modelDetail.months} months</div>
            <div>Created: {formatDate(modelDetail.created_at)}</div>
          </div>
        </div>
        {modelDetail.regression_results && (
          <RegressionResultsView results={modelDetail.regression_results} />
        )}
      </div>
    )
  }

  if (detailLoading) return <LoadingSpinner />

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      <div className="p-4 border-b border-border">
        <h2 className="text-lg font-semibold">Saved Factor Models</h2>
      </div>
      <table className="data-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Description</th>
            <th className="text-center">Factors</th>
            <th className="text-center">Months</th>
            <th>Created</th>
            <th className="text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {data.models.map((model: any) => (
            <tr key={model.id}>
              <td>
                <button
                  onClick={() => setSelectedModelId(model.id)}
                  className="font-medium text-primary hover:underline"
                >
                  {model.name}
                </button>
              </td>
              <td className="text-sm text-muted-foreground max-w-xs truncate">
                {model.description || '-'}
              </td>
              <td className="text-center text-sm">{model.factor_names?.length || 0}</td>
              <td className="text-center text-sm">{model.months}</td>
              <td className="text-sm">{formatDate(model.created_at)}</td>
              <td className="text-right">
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    if (confirm('Delete this model?')) deleteMutation.mutate(model.id)
                  }}
                  className="px-2 py-1 text-xs text-destructive hover:bg-destructive/10 rounded"
                >
                  Delete
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/* ─── Portfolio Exposures Tab ─── */

function PortfolioExposuresTab({ months, factors: sel }: { months: number; factors: string[] }) {
  const fq = sel.length ? `&factors=${encodeURIComponent(sel.join(','))}` : ''
  const { data, isLoading } = useQuery({
    queryKey: ['factor-portfolio', months, sel.join(',')],
    queryFn: () => api.get(`/factors/portfolio?months=${months}${fq}`).then((r) => r.data),
  })

  if (isLoading) return <LoadingSpinner />
  if (data?.error) return <Empty msg={data.error} />
  if (!data?.funds?.length) return <Empty msg="No portfolio factor exposures available" />

  const factors = data.factors as string[]

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      <div className="p-4 border-b border-border">
        <h2 className="text-lg font-semibold">Portfolio Factor Exposures</h2>
        <p className="text-xs text-muted-foreground mt-1">
          Beta / R-squared / p-value for each fund-factor pair
          {!sel.length && data.total_available > factors.length &&
            ` — showing ${factors.length} of ${data.total_available} factors; search above to filter`}
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="data-table">
          <thead>
            <tr>
              <th>Fund</th>
              <th className="text-right">Weight</th>
              {factors.map((f) => (
                <th key={f} className="text-center whitespace-nowrap">{f}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.funds.map((fund: any) => (
              <tr key={fund.fund_name}>
                <td className="font-medium">{fund.fund_name}</td>
                <td className="text-right font-mono">{formatPercent(fund.weight)}</td>
                {factors.map((f) => {
                  const exp = fund.exposures?.[f]
                  if (!exp) return <td key={f} className="text-center text-muted-foreground">-</td>
                  const sigColor = exp.p_value < 0.05 ? 'text-foreground' : 'text-muted-foreground'
                  return (
                    <td key={f} className={`text-center text-xs ${sigColor}`}>
                      <div className="font-mono">{formatNumber(exp.beta, 2)}</div>
                      <div className="text-muted-foreground">
                        R²={formatNumber(exp.r_squared, 2)} p={formatNumber(exp.p_value, 3)}
                      </div>
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

/* ─── Helpers ─── */

function Empty({ msg }: { msg: string }) {
  return (
    <div className="bg-card border border-border rounded-lg p-8 text-center text-muted-foreground">
      {msg}
    </div>
  )
}

function getVifColor(vif: number | null | undefined): string {
  if (vif == null) return ''
  if (vif > 10) return 'text-red-600 font-semibold'
  if (vif > 5) return 'text-yellow-600 font-semibold'
  return ''
}

function getPValueColor(pValue: number | null | undefined): string {
  if (pValue == null) return ''
  if (pValue < 0.05) return 'text-green-600 font-semibold'
  if (pValue < 0.10) return 'text-yellow-600'
  return 'text-muted-foreground'
}

function formatPValue(pValue: number | null | undefined): string {
  if (pValue == null) return '-'
  if (pValue < 0.001) return '<0.001'
  return pValue.toFixed(3)
}
